import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "@/components/ui/layout";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Landing, { LandingRoute } from "./pages/Landing";
import Brands from "./pages/Brands";
import Courts from "./pages/Courts";
import Videos from "./pages/Videos";
import VideoDetail from "./pages/VideoDetail";
import Library from "./pages/Library";
import Profile from "./pages/Profile";
import Cart from "./pages/Cart";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./pages/AdminDashboard";
import ManagerTools from "./pages/ManagerTools";
import TransactionHistory from "./pages/TransactionHistory";
import EditProfile from "./pages/EditProfile";

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  return isAuthenticated
    ? <>{children}</>
    : (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }}
      />
    );
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  // const { isAuthenticated } = useAuth();
  // return !isAuthenticated ? <>{children}</> : <Navigate to="/landing" replace />;
  return <>{children}</>;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/landing" replace />} />
    <Route path="/landing" element={<LandingRoute />} />

    {/* Public Routes */}
    <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
    <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
    <Route path="/reset-password" element={<ResetPassword />} />

    {/* Protected Routes */}
    <Route element={<Layout />}>
      <Route path="/brands" element={<PublicRoute><Brands /></PublicRoute>} /> {/* Guess can access */}
      <Route path="/brands/:brandSlug/courts" element={<PublicRoute><Courts /></PublicRoute>} /> {/* Guess can access */}
      <Route path="/brands/:brandSlug/courts/:courtSlug/videos" element={<PublicRoute><Videos /></PublicRoute>} /> {/* Guess can access */}
      <Route path="/videos/:videoId" element={<PublicRoute><VideoDetail /></PublicRoute>} /> {/* Guess can access */}
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
      <Route path="/transaction-history" element={<ProtectedRoute><TransactionHistory /></ProtectedRoute>} />
      <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
      <Route path="/admin-dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/manager-tools" element={<ProtectedRoute><ManagerTools /></ProtectedRoute>} />
    </Route>

    {/* Catch-all */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
